import { useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { LineSummary } from 'types/api/loan'
import { HelpIcon } from 'assets/icons'
import {
  CREDIT_PRODUCT,
  getCreditHistory,
  getCreditLinesSummary,
  getOffer
} from 'api/advances'
import { CreditLinesModal } from 'components/LineModals'
import { useActiveCreditsStore, useLineSummaryStore } from 'store'
import { OFFER_ERROR_SLUGS } from 'constants/credit'

import ClientInDebtModal from 'components/LineModals/ClientInDebtModal'
import CreditScoreCard from '../CreditScoreCard'

import GenerateLineInfo from './GenerateLineInfo'
import ProgressComponent from './ProgressInfo'

const { LOANS, WALLET } = CREDIT_PRODUCT
const { inArrears, badScore, lineIsDepleted, noProposalAvailable } =
  OFFER_ERROR_SLUGS

const CreditLines = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [advanceSummary, setAdvanceSummary] = useState<LineSummary>(null)
  const [loanSummary, setLoanSummary] = useState<LineSummary>(null)
  const [isFetchingAdvance, setIsFetchingAdvance] = useState<boolean>(false)
  const [isFetchingLoan, setIsFetchingLoan] = useState<boolean>(false)
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [clientDebtModal, setClientDebtModal] = useState<boolean>(false)

  const { updateLineSummary, lineSummary } = useLineSummaryStore()
  const { updateActiveAdvances, updateActiveLoans } = useActiveCreditsStore()

  useEffect(() => {
    Promise.all([
      getCreditLinesSummary(WALLET),
      getCreditLinesSummary(LOANS),
      getCreditHistory({
        creditProduct: WALLET,
        states: 'active'
      }),
      getCreditHistory({
        creditProduct: LOANS,
        states: 'active'
      })
    ])
      .then((res) => {
        const [advanceSummary, loanSummary, activeAdvances, activeLoans] = res

        // summaries
        setAdvanceSummary(advanceSummary)
        setLoanSummary(loanSummary)

        updateLineSummary({
          ...lineSummary,
          advanceAllocatedAmount: advanceSummary.allocatedAmount,
          advanceApprovedAmount: advanceSummary.approvedAmount,
          loanAllocatedAmount: loanSummary.allocatedAmount,
          loanApprovedAmount: loanSummary.approvedAmount,
          advanceRemainingAmount: advanceSummary.remainingAmount,
          loanRemainingAmount: loanSummary.remainingAmount
        })

        // active credits
        updateActiveAdvances(activeAdvances.data)
        updateActiveLoans(activeLoans.data)
      })
      .finally(() => setIsLoading(false))
  }, [])

  const handleFetchButton = async (type: keyof typeof CREDIT_PRODUCT) => {
    const isLoan = CREDIT_PRODUCT[type] === LOANS

    const fetcherState = isLoan ? isFetchingLoan : isFetchingAdvance

    if (fetcherState) return

    const setSummary = isLoan ? setLoanSummary : setAdvanceSummary
    const fetcherSetter = isLoan ? setIsFetchingLoan : setIsFetchingAdvance
    isLoan ? updateActiveLoans([]) : updateActiveAdvances([])

    fetcherSetter(true)

    const offerRes = await getOffer(CREDIT_PRODUCT[type])

    if (offerRes.success) {
      const summary = offerRes.data.result

      let newSummary: any = {
        remainingAmount: isLoan
          ? summary.personal_loans.max_amount
          : summary.cash_advances.max_amount,
        approvedAmount: isLoan
          ? summary.personal_loans.max_amount
          : summary.cash_advances.max_amount,
        isValid: true
      }

      setSummary((prev) => ({
        ...prev,
        ...newSummary
      }))

      // UPDATE ACTIVE CREDITS (to get the right sum of other line period credits)
      updateActiveCredits(isLoan)
    } else {
      const { code } = offerRes.error

      if ([badScore, lineIsDepleted, noProposalAvailable].includes(code)) {
        let newSummary: any = {
          remainingAmount: 0,
          allocatedAmount: 0,
          isValid: true,
          isZero: true
        }

        setSummary((prev) => ({
          ...prev,
          ...newSummary
        }))
      }

      if (code === inArrears) {
        setClientDebtModal(true)
      }
    }

    fetcherSetter(false)
  }

  const updateActiveCredits = async (isLoan: boolean) => {
    if (isLoan) {
      const res = await getCreditHistory({
        creditProduct: LOANS,
        states: 'active'
      })

      updateActiveLoans(res.data)
    } else {
      const res = await getCreditHistory({
        creditProduct: WALLET,
        states: 'active'
      })

      updateActiveAdvances(res.data)
    }
  }

  if (isLoading) return <Skeleton height={220} className='w-full' />

  return (
    <>
      <CreditScoreCard
        title={
          <span className='w-full flex justify-between'>
            <span>Líneas de crédito</span>{' '}
            <button
              type='button'
              onClick={() => setModalIsOpen(true)}
              className={`flex shrink-0 justify-center items-center w-6 h-6 rounded-full
}`}
            >
              <p className='sr-only'>?</p>
              <HelpIcon variant='GREEN' className='h-5 w-5' />
            </button>
          </span>
        }
      >
        {loanSummary.isValid ? (
          <ProgressComponent
            className='mt-4'
            type='LOANS'
            summary={loanSummary}
          />
        ) : (
          <GenerateLineInfo
            action={() => handleFetchButton('LOANS')}
            className='mt-4'
            isFetching={isFetchingLoan}
            type='LOANS'
          />
        )}
        {advanceSummary.isValid ? (
          <ProgressComponent
            className='mt-4'
            type='WALLET'
            summary={advanceSummary}
          />
        ) : (
          <GenerateLineInfo
            action={() => handleFetchButton('WALLET')}
            className='mt-4'
            isFetching={isFetchingAdvance}
            type='WALLET'
          />
        )}
      </CreditScoreCard>
      <CreditLinesModal
        isShown={modalIsOpen}
        setModalFunction={setModalIsOpen}
      />
      <ClientInDebtModal
        isOpen={clientDebtModal}
        setIsOpen={setClientDebtModal}
      />
    </>
  )
}

export default CreditLines
